import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import translations from './students.psa.i18n';
import * as css from './students.module.scss';
import { PraksisstedApiContext } from '../api/PraksisstedApiContext';
import { useLoader } from '../api/useLoader';
import Spinner from '../components/spinner/spinner';
import PageLayout from '../pagelayout';
import {
  Table,
  TableCellTd,
  TableCellTh,
  TableHeader,
  TableMain,
  TableRow,
} from '../components/table/alpTable';
import {
  EmneDto,
  MinimalPraksisstedDto,
  StudentbrukerDto,
  StudentPaaPraksisstedDto,
} from '../api/generated/praksissted-generated-api';
import PanelHeader from '../components/panelheader/panelHeader';
import {
  OnStedHoverCard,
  StedType,
} from '../components/hierarchy/onhoverhierarchyinfo';
import { Route, Routes } from 'react-router-dom';
import StudentbrukerprofilPage from '../userprofile/studentbrukerprofilpage.psa';
import PageNotFoundPage from '../pages/pagenotfound';
import { formatLocaleNo } from '../date.utils';

const StudentPage = () => (
  <Routes>
    <Route
      path="/studentbrukerprofil/:id"
      element={<StudentbrukerprofilPage />}
    />
    <Route path="/" element={<Students />} />
    <Route path="/*" element={<PageNotFoundPage />} />
  </Routes>
);

const Students = () => {
  const { praksisstedApi } = useContext(PraksisstedApiContext);

  const { data, loading } = useLoader(
    () => praksisstedApi.studenterGet(),
    [],
    true,
    true,
  );

  const { formatMessage } = useIntl();
  const labels = {
    title: formatMessage({ id: translations.title.id }),
    subtitle: formatMessage({ id: translations.subtitle.id }),
    aktive: formatMessage({ id: translations.aktive.id }),
    fremtidige: formatMessage({ id: translations.fremtidige.id }),
    gamle: formatMessage({ id: translations.gamle.id }),
  };

  const tableHeaders = [
    { title: formatMessage({ id: translations.student.id }) },
    { title: formatMessage({ id: translations.periode.id }) },
    { title: formatMessage({ id: translations.emne.id }) },
    { title: formatMessage({ id: translations.praksissted.id }) },
    { title: formatMessage({ id: translations.studiested.id }) },
    { title: formatMessage({ id: translations.epost.id }) },
    { title: formatMessage({ id: translations.telefon.id }) },
  ];

  const getHeaders = () =>
    tableHeaders.map(({ title }, index) => (
      <TableCellTh key={title} colIndex={index}>
        {title}
      </TableCellTh>
    ));

  // TODO: group students by time and emne!
  //       where time is current, future and past activities
  return (
    <PageLayout
      isIndented={true}
      title={labels.title}
      subtitle={labels.subtitle}
    >
      {loading && <Spinner />}
      {data && (
        <>
          <PanelHeader title={labels.aktive} />
          <StudentTabell
            headerItems={getHeaders()}
            headerLabels={tableHeaders}
            studenter={data.aktive!!}
          />
          <div className={css.space40} />
          <PanelHeader title={labels.fremtidige} />
          <StudentTabell
            headerItems={getHeaders()}
            headerLabels={tableHeaders}
            studenter={data.fremtidige!!}
          />
          <div className={css.space40} />
          <PanelHeader title={labels.gamle} />
          <StudentTabell
            headerItems={getHeaders()}
            headerLabels={tableHeaders}
            studenter={data.gamle!!}
          />
        </>
      )}
    </PageLayout>
  );
};

const StudentTabell: React.FC<
  React.PropsWithChildren<{
    headerItems: JSX.Element[];
    headerLabels: { title: string }[];
    studenter: StudentPaaPraksisstedDto[];
  }>
> = ({ headerItems, headerLabels, studenter }) => {
  return (
    <Table>
      <TableHeader>
        <TableRow top>{headerItems}</TableRow>
      </TableHeader>
      <TableMain>
        {studenter.map((studentPaaPraksissted) => (
          <StudentRad
            headers={headerLabels}
            student={studentPaaPraksissted.student}
            startDato={studentPaaPraksissted.startDato}
            sluttDato={studentPaaPraksissted.sluttDato}
            praksissted={studentPaaPraksissted.praksissted}
            emne={studentPaaPraksissted.emne}
          />
        ))}
      </TableMain>
    </Table>
  );
};

export const StudentRad: React.FC<
  React.PropsWithChildren<{
    headers: { title: string }[];
    student: StudentbrukerDto;
    startDato: string;
    sluttDato: string;
    praksissted: MinimalPraksisstedDto;
    emne: EmneDto;
  }>
> = ({ headers, student, startDato, sluttDato, praksissted, emne }) => {
  let tabHeader = 0;

  const norwegianStartDate = formatLocaleNo(startDato);
  const norwegianSluttDate = formatLocaleNo(sluttDato);

  return (
    <TableRow id={''}>
      <TableCellTd header={headers[tabHeader++].title}>
        {student.etternavn}, {student.fornavn}
      </TableCellTd>
      <TableCellTd header={headers[tabHeader++].title}>
        {norwegianStartDate + ' - ' + norwegianSluttDate}
      </TableCellTd>
      <TableCellTd header={headers[tabHeader++].title}>{emne.kode}</TableCellTd>
      <TableCellTd header={headers[tabHeader++].title}>
        <div className={css.flex}>
          {praksissted.navn}
          <OnStedHoverCard
            id={praksissted.id}
            navn={praksissted.navn}
            sted={StedType.PRAKSISSTED}
          />
        </div>
      </TableCellTd>
      <TableCellTd header={headers[tabHeader++].title}>
        {student.studiestednavn}
      </TableCellTd>
      <TableCellTd header={headers[tabHeader++].title}>
        {student.epost}
      </TableCellTd>
      <TableCellTd header={headers[tabHeader++].title}>
        {student.telefonnummer}
      </TableCellTd>
    </TableRow>
  );
};

export default StudentPage;
